/**
 * 监控配置：阈值、窗口、限流参数、噪音过滤规则
 *
 * 本文件只放纯常量，不读环境变量 —— 环境相关配置（开关、上报地址）
 * 由 index.ts 读取后注入 reporter，保持本文件无副作用。
 */

export const MONITOR_CONFIG = {
  /** 微批 flush 间隔（ms），决定告警最坏延迟 */
  FLUSH_INTERVAL: 5_000,
  /** 同指纹冷却窗口（ms） */
  DEDUPE_WINDOW: 60_000,
  /** 令牌桶容量 */
  BUCKET_CAPACITY: 10,
  /** 令牌补充间隔（ms）：6s 补 1 个 = 10 条/分钟，只占钉钉 20 条/分钟限额的一半 */
  TOKEN_REFILL_INTERVAL: 6_000,
  /** 单页面会话总上报上限，防止死循环报错吃光机器人配额 */
  SESSION_REPORT_LIMIT: 50,
  /** 单条钉钉消息最多合并的事件数 */
  MAX_EVENTS_PER_MESSAGE: 5,
  /** 堆栈保留行数 */
  STACK_LINES: 3,
  /** 聚合表条目最长闲置时间（ms），超时清理防内存泄漏 */
  ENTRY_TTL: 300_000,
  /** 单条钉钉消息最大字符数，超长会整条发送失败 */
  MAX_MESSAGE_LENGTH: 4_000,
  /** 告警中展示的请求参数最大长度 */
  MAX_PAYLOAD_LENGTH: 300,
  /** 性能告警阈值 */
  THRESHOLD: {
    LCP: 4_000,
    FCP: 3_000,
    CLS: 0.25,
    INP: 500,
    ROUTE_CHANGE: 2_000,
    SLOW_API: 5_000,
  },
} as const;

/**
 * 慢接口阈值的按接口覆盖：键为归一化后的路径，值为毫秒，**0 表示不限制**。
 *
 * SLOW_API 只适合秒级返回的接口。有些接口天生就慢，按通用阈值报只会刷屏，
 * 需要在这里单独放宽或直接豁免。路径写法跟告警里的一致（数字段已被替成 {id}）。
 */
export const SLOW_API_OVERRIDES: Record<string, number> = {
  // 暂无：/products/sync 原先在此豁免，后端改为异步提交后已恢复默认阈值
};

/**
 * 命中即丢弃的噪音错误
 * - Script error.：跨域脚本报错，无堆栈无位置，无法定位
 * - ResizeObserver loop：浏览器已知无害警告，Element Plus 表格频繁触发
 */
export const IGNORED_ERROR_PATTERNS: RegExp[] = [/^Script error\.?$/i, /ResizeObserver loop/i];

/**
 * 钉钉机器人「自定义关键词」安全设置所需的关键词。
 *
 * 机器人开启该模式后，**消息内容必须包含至少一个关键词才会被投递**，
 * 否则钉钉直接返回 310000 keywords not in content。
 * 这里的取值必须与钉钉后台配置逐字一致；formatter 会保证每条消息标题都带上其一。
 *
 * ERROR 对应 level=error 的告警，PERFORMANCE 对应 level=warn 的性能类告警。
 */
export const DINGTALK_KEYWORDS = {
  ERROR: '异常报错',
  PERFORMANCE: '性能问题',
} as const;

/**
 * 请求参数中需要脱敏的字段名（不区分大小写，子串匹配）。
 *
 * 告警会发进钉钉群，参数原样带出去等于把凭证和用户数据发给群里所有人，
 * 命中这些关键字的值一律替换为 ***。
 */
export const SENSITIVE_KEY_PATTERN = /pass|token|secret|sign|auth|credential|cookie|session/i;
